import axios from 'axios';
import { getSubscriberId } from '../App';

// Debug configuration
const DEBUG = process.env.REACT_APP_DEBUG === 'true';

// Dynamic API URL detection - automatically uses the same domain as the frontend
const getApiUrl = () => {
  // If explicitly set in environment, use that (for local development)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For production: automatically use the same domain as the frontend
  const currentOrigin = window.location.origin;
  
  // If running on localhost, assume backend is on port 8000
  if (currentOrigin.includes('localhost')) {
    return 'http://localhost:8000';
  }
  
  // For production domains, assume API is on the same domain
  return currentOrigin;
};

const API_URL = getApiUrl();

// Mobile detection and configuration
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const isSlowConnection = navigator.connection && (
  navigator.connection.effectiveType === 'slow-2g' || 
  navigator.connection.effectiveType === '2g' ||
  navigator.connection.saveData
);

// Mobile-optimized configuration
const MOBILE_CONFIG = {
  timeout: isMobile ? 15000 : 10000, // Longer timeout for mobile
  maxRetries: isMobile ? 3 : 1,
  retryDelay: isSlowConnection ? 2000 : 1000,
  videoTimeout: isMobile ? 20000 : 15000,
  chunkSize: isSlowConnection ? 1024 * 512 : 1024 * 1024 // 512KB for slow connections
};

// Create an axios instance with mobile-optimized interceptors
const api = axios.create({
  baseURL: API_URL,
  timeout: MOBILE_CONFIG.timeout,
  withCredentials: false,
  // Add mobile-specific headers
  headers: {
    'Cache-Control': isMobile ? 'max-age=300' : 'no-cache',
    'X-Mobile-Device': isMobile ? 'true' : 'false',
    'X-Connection-Type': navigator.connection?.effectiveType || 'unknown'
  }
});

// Mobile-optimized retry mechanism
const retryRequest = async (config, attempt = 1) => {
  try {
    const response = await axios(config);
    return response;
  } catch (error) {
    if (attempt < MOBILE_CONFIG.maxRetries && shouldRetry(error)) {
      console.log(`🔄 Retry attempt ${attempt + 1}/${MOBILE_CONFIG.maxRetries} for ${config.url}`);
      await new Promise(resolve => setTimeout(resolve, MOBILE_CONFIG.retryDelay * attempt));
      return retryRequest(config, attempt + 1);
    }
    throw error;
  }
};

// Determine if request should be retried
const shouldRetry = (error) => {
  if (!error.response) return true; // Network errors
  const status = error.response.status;
  return status >= 500 || status === 408 || status === 429; // Server errors, timeout, rate limit
};

// Request interceptor with mobile optimizations
api.interceptors.request.use(config => {
  // Add timestamp to track request duration
  config.timestamp = new Date().getTime();
  
  // Add a unique request ID
  config.requestId = `req-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  
  // Add mobile-specific optimizations
  if (isMobile) {
    config.timeout = MOBILE_CONFIG.timeout;
    
    // Add connection quality headers
    if (navigator.connection) {
      config.headers['X-Network-Type'] = navigator.connection.effectiveType;
      config.headers['X-Downlink'] = navigator.connection.downlink;
      config.headers['X-RTT'] = navigator.connection.rtt;
    }
    
    // Optimize for slow connections
    if (isSlowConnection) {
      config.headers['Accept-Encoding'] = 'gzip, deflate, br';
      config.headers['X-Prefer-Compressed'] = 'true';
    }
  }
  
  return config;
}, error => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Response interceptor with minimal logging
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    const requestId = error.config?.requestId || 'unknown';
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error(`❌ API ERROR [${requestId}]: ${error.response.status} ${error.config.method.toUpperCase()} ${error.config.url}`, {
        data: error.response.data,
        headers: error.response.headers,
        timestamp: new Date().toISOString(),
        duration: error.config.timestamp ? `${Date.now() - error.config.timestamp}ms` : 'unknown'
      });
      
      // Special handling for 404 errors
      if (error.response.status === 404) {
        // Check if this is a view or like tracking endpoint
        if (error.config.url.includes('/view') || error.config.url.includes('/like')) {
          console.log(`ℹ️ Tracking endpoint not found: ${error.config.url}`);
          console.log(`ℹ️ This is normal if tracking is disabled or not configured.`);
        } else {
          console.error(`⚠️ 404 NOT FOUND ERROR: The endpoint ${error.config.url} was not found`);
          console.error(`💡 TROUBLESHOOTING 404 ERRORS:
            1. Check if the backend server is running at ${API_URL}
            2. Verify the API URL in your .env file is correct
            3. Make sure the endpoint path (${error.config.url}) is correct
            4. Check if CORS is properly configured on the backend
            5. Try accessing ${API_URL}${error.config.url} directly in your browser
            6. Check the backend logs for any errors`);
        }
        
        // Try to fetch the root endpoint to check if the server is running
        fetch(API_URL)
          .then(response => {
            console.log(`✅ API server is running and returned status: ${response.status}`);
            return response.text();
          })
          .then(text => {
            if (text.includes('<!DOCTYPE html>')) {
              console.error(`⚠️ API server returned HTML instead of JSON. You might be connecting to the wrong server.`);
            } else {
              try {
                const json = JSON.parse(text);
                console.log('✅ API root endpoint returned valid JSON:', json);
                console.error(`⚠️ The server is running, but the specific endpoint ${error.config.url} was not found.`);
              } catch (e) {
                console.warn('⚠️ API did not return valid JSON:', text.substring(0, 100));
              }
            }
          })
          .catch(err => {
            console.error(`❌ API server is not running or not accessible at ${API_URL}:`, err.message);
          });
      }
      
      // Add additional debug info
      if (DEBUG) {
        console.error(`🔍 DEBUG ERROR DETAILS [${requestId}]:`, {
          status: error.response.status,
          statusText: error.response.statusText,
          contentType: error.response.headers['content-type'],
          url: `${error.config.baseURL || ''}${error.config.url}`,
          method: error.config.method.toUpperCase(),
          params: error.config.params,
          data: error.config.data
        });
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error(`❌ API NO RESPONSE [${requestId}]: ${error.config.method.toUpperCase()} ${error.config.url}`, {
        request: error.request,
        timestamp: new Date().toISOString(),
        duration: error.config.timestamp ? `${Date.now() - error.config.timestamp}ms` : 'unknown'
      });
      
      // Add additional debug info for network errors
      if (DEBUG) {
        console.error(`🔍 DEBUG NETWORK ERROR [${requestId}]:`, {
          message: error.message,
          url: `${error.config.baseURL || ''}${error.config.url}`,
          method: error.config.method.toUpperCase(),
          timeout: error.config.timeout,
          withCredentials: error.config.withCredentials
        });
        
        // Provide troubleshooting tips
        console.error(`💡 TROUBLESHOOTING TIPS:
          1. Check if the backend server is running
          2. Verify the API URL: ${API_URL}
          3. Check for CORS issues (try opening the URL directly in browser)
          4. Check network connectivity
          5. Verify there are no proxy or firewall issues`);
      }
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error(`❌ API REQUEST ERROR [${requestId}]: ${error.message}`, {
        config: error.config,
        timestamp: new Date().toISOString()
      });
      
      if (DEBUG) {
        console.error(`🔍 DEBUG CONFIG ERROR [${requestId}]:`, {
          message: error.message,
          stack: error.stack,
          config: error.config
        });
      }
    }
    return Promise.reject(error);
  }
);

// Removed API connection test for better performance

/**
 * Fetch videos with pagination, ordering, and filtering (mobile-optimized)
 * @param {string} cursor - Pagination cursor
 * @param {number} limit - Number of videos to fetch
 * @param {string} publicationId - ID of the publication to filter by
 * @param {string} orderBy - Ordering method: 'random', 'popularity', 'recency'
 * @param {Object} filters - Filtering criteria (min_likes, min_views)
 * @returns {Promise} - Promise with videos data
 */
export const fetchVideos = async (
  cursor = null, 
  limit = 10, 
  publicationId = null, 
  orderBy = null,
  filters = {}
) => {
  try {
    const params = { limit };
    
    // Mobile optimization: reduce batch size for slow connections
    if (isMobile && isSlowConnection && limit > 5) {
      params.limit = 5;
      console.log('📱 Reduced batch size for slow mobile connection');
    }
    
    // Add pagination cursor if provided
    if (cursor) {
      params.cursor = cursor;
    }
    
    // Add publication filter if provided
    if (publicationId) {
      params.publication = publicationId;
    }
    
    // Add ordering method if provided
    if (orderBy) {
      params.order_by = orderBy;
    }
    
    // Add filtering criteria if provided
    if (filters.minLikes) {
      params.min_likes = filters.minLikes;
    }
    
    if (filters.minViews) {
      params.min_views = filters.minViews;
    }
    
    // Use retry mechanism for mobile devices
    if (isMobile) {
      const config = {
        method: 'get',
        url: '/api/videos',
        baseURL: API_URL,
        params,
        timeout: MOBILE_CONFIG.timeout,
        headers: {
          'X-Mobile-Request': 'true',
          'X-Request-Type': 'video-fetch'
        }
      };
      
      const response = await retryRequest(config);
      return response.data;
    } else {
      const response = await api.get('/api/videos', { params });
      return response.data;
    }
  } catch (error) {
    console.error('❌ Error fetching videos:', error);
    
    // Mobile-specific error handling
    if (isMobile) {
      console.log('📱 Mobile video fetch failed, attempting fallback...');
      
      // Try with reduced parameters for mobile fallback
      try {
        const fallbackParams = { limit: Math.min(limit, 3) };
        if (cursor) fallbackParams.cursor = cursor;
        
        const fallbackResponse = await api.get('/api/videos', { 
          params: fallbackParams,
          timeout: MOBILE_CONFIG.timeout * 2
        });
        
        console.log('✅ Mobile fallback successful');
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error('❌ Mobile fallback also failed:', fallbackError);
      }
    }
    
    throw error;
  }
};

/**
 * Like a video
 * @param {string} videoId - ID of the video to like
 * @returns {Promise} - Promise with updated video data
 */
export const likeVideo = async (videoId) => {
  try {
    const response = await api.post(`/api/videos/${videoId}/like`);
    return response.data;
  } catch (error) {
    // Return a friendly response with default values
    return {
      id: videoId,
      likes: 1,  // Default like count
      message: "Like tracking is disabled but like was registered locally"
    };
  }
};

// Helper functions for detailed view tracking
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Design 046 R1: one stable session id per browser session, so every view event in a session
// shares it — required for videos-per-session / session-length metrics. Falls back to a fresh id
// if sessionStorage is unavailable (private mode).
const getOrCreateSessionId = () => {
  try {
    let sid = sessionStorage.getItem('vusub_session_id');
    if (!sid) {
      sid = generateSessionId();
      sessionStorage.setItem('vusub_session_id', sid);
    }
    return sid;
  } catch {
    return generateSessionId();
  }
};

const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return 'mobile';
  } else if (/tablet|ipad/i.test(userAgent)) {
    return 'tablet';
  } else {
    return 'desktop';
  }
};

/**
 * View a video with detailed metrics tracking
 * @param {string} videoId - ID of the video viewed
 * @param {Object} viewData - Optional detailed view data
 * @returns {Promise} - Promise with updated video data
 */
export const viewVideo = async (videoId, viewData = {}) => {
  if (!videoId) {
    console.warn('📊 View tracking skipped: videoId is empty');
    return { views: 0, message: 'No videoId provided' };
  }
  try {
    const subscriberId = getSubscriberId();
    let accountId = '';
    try { accountId = sessionStorage.getItem('vusub_account_id') || ''; } catch { /* ignore */ }

    // Create comprehensive view data
    const trackingData = {
      video_id: videoId,
      subscriber_id: subscriberId,
      account_id: accountId,
      watch_time_seconds: viewData.watchTimeSeconds || 0,
      watch_percentage: viewData.watchPercentage || 0,
      video_duration: viewData.videoDuration || null,
      session_id: viewData.sessionId || getOrCreateSessionId(),
      device_type: getDeviceType(),
      event_type: viewData.eventType || 'view',
      publication_id: viewData.publication_id || '',
      ...viewData
    };
    
    const response = await api.post(`/api/videos/${videoId}/view`, trackingData);
    return response.data;
  } catch (error) {
    console.error('❌ Error tracking video view:', error);
    // Return a friendly response with default values - don't break video playback
    return {
      id: videoId,
      views: 1,  // Default view count
      message: "View tracking failed but video playback continues"
    };
  }
};

/**
 * Get detailed API status
 * @returns {Promise} - Promise with API status data
 */
export const getApiStatus = async () => {
  try {
    const response = await api.get('/');
    return response.data;
  } catch (error) {
    console.error('Error checking API status:', error);
    throw error;
  }
};

/**
 * Track an ad impression/completion/click for an agency video.
 * Requires a valid CIAM idToken (Bearer). Silently ignores errors.
 *
 * @param {string} agencyVideoId - The agency_video_id field from VideoMetadata
 * @param {string} idToken       - Bearer token from MSAL acquireTokenSilent
 * @param {object} data          - { event_type, watch_percentage, publication_id }
 */
export const trackAdImpression = async (agencyVideoId, idToken, data = {}) => {
  if (!agencyVideoId || !idToken) return;
  try {
    await axios.post(
      `${API_URL}/api/ads/${agencyVideoId}/impression`,
      {
        event_type: data.event_type || 'impression',
        watch_percentage: data.watch_percentage || 0,
        publication_id: data.publication_id || '',
      },
      {
        headers: { Authorization: `Bearer ${idToken}` },
        timeout: 5000,
      }
    );
  } catch (e) {
    // Fire-and-forget: never surface errors to the user
  }
};

// Removed API status check on initialization for better performance

export const getPublications = async (idToken) => {
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const response = await axios.get(`${API_URL}/api/publications`, { headers, timeout: 10000 });
  return response.data.publications || [];
};

export const getSubscriberBalance = async (publicationId, idToken) => {
  if (!publicationId || !idToken) {
    throw new Error('publicationId and idToken are required');
  }
  const response = await axios.get(
    `${API_URL}/api/subscriber/balance/${encodeURIComponent(publicationId)}`,
    {
      headers: { Authorization: `Bearer ${idToken}` },
      timeout: 10000,
    }
  );
  return response.data;
};

// Fire-and-forget invite-click beacon. Records the click for funnel analytics and
// returns the click_id (for conversion linkage). MUST never throw or block render —
// any failure resolves to null so callers can safely `await` it inside an effect.
export const postInviteClick = async (token, tokenType, publicationId) => {
  try {
    const body = { token, token_type: tokenType };
    if (publicationId) body.publication_id = publicationId;
    const response = await axios.post(`${API_URL}/api/invite/click`, body, { timeout: 8000 });
    return response.data?.click_id || null;
  } catch {
    return null;
  }
};
